import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
  TooltipItem,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/axiosInstance';
import {
  format,
  startOfWeek,
  eachDayOfInterval,
  eachMonthOfInterval,
  isWithinInterval,
  differenceInCalendarDays,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import { Box } from '@mui/material';
import { PeriodType } from '../periodToggle';
import { formatCurrency } from '../../utils/formatMoney';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface CategoryStat {
  name: string;
  total: number;
  date: string;
}

interface CategoryBarProps {
  dataType: 'income' | 'expense';
  period: PeriodType;
  from?: string;
  to?: string;
}

const hashToColor = (s: string, alpha = 0.6) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const r = 40 + (h % 160);
  const g = 40 + ((h >> 8) % 160);
  const b = 40 + ((h >> 16) % 160);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const pct = (part: number, sum: number) => (sum > 0 ? Math.round((part / sum) * 100) : 0);

type Built = { labels: string[]; datasets: { name: string; data: number[]; sum: number }[] };

const CategoryBar: React.FC<CategoryBarProps> = ({ dataType, period, from, to }) => {
  const endpoint = dataType === 'income' ? 'categories-income/stats' : 'categories-expense/stats';

  const queryParams = useMemo(() => {
    const params: Record<string, string> = { period };

    if (period === 'custom') {
      if (from) params.from = from;
      if (to) params.to = to;
    }

    return params;
  }, [period, from, to]);

  const { data = [] } = useQuery<CategoryStat[]>({
    queryKey: ['category-bar', dataType, period, from, to],
    queryFn: () => api.get(`/${endpoint}`, { params: queryParams }).then(r => r.data),
  });

  const built: Built = useMemo(() => {
    const now = new Date();

    if (period === 'years') {
      const yearsSet = new Set<number>();
      data.forEach(d => yearsSet.add(new Date(d.date).getFullYear()));
      const years = Array.from(yearsSet).sort((a, b) => a - b);

      const labels = years.map(String);
      const totalsByCategory: Record<string, number[]> = {};

      data.forEach(d => {
        const y = new Date(d.date).getFullYear();
        const idx = years.indexOf(y);
        if (idx < 0) return;

        if (!totalsByCategory[d.name]) totalsByCategory[d.name] = new Array(years.length).fill(0);
        totalsByCategory[d.name][idx] += Number(d.total);
      });

      const datasets = Object.entries(totalsByCategory)
        .map(([name, arr]) => ({
          name,
          data: arr,
          sum: arr.reduce((a, b) => a + b, 0),
        }))
        .sort((a, b) => b.sum - a.sum);

      return { labels, datasets };
    }

    if (period === 'year') {
      const y = now.getFullYear();
      const months = Array.from({ length: 12 }, (_, i) => i).reverse();
      const labels = months.map(m => format(new Date(y, m, 1), 'LLLL', { locale: ru }));

      const totalsByCategory: Record<string, number[]> = {};

      data.forEach(d => {
        const dt = new Date(d.date);
        if (dt.getFullYear() !== y) return;

        const m = dt.getMonth();
        const idx = months.indexOf(m);
        if (idx < 0) return;

        if (!totalsByCategory[d.name]) totalsByCategory[d.name] = new Array(12).fill(0);
        totalsByCategory[d.name][idx] += Number(d.total);
      });

      const datasets = Object.entries(totalsByCategory)
        .map(([name, arr]) => ({
          name,
          data: arr,
          sum: arr.reduce((a, b) => a + b, 0),
        }))
        .sort((a, b) => b.sum - a.sum);

      return { labels, datasets };
    }

    if (period === 'month') {
      const y = now.getFullYear();
      const mo = now.getMonth();
      const daysPassed = now.getDate();
      const weeksCount = Math.ceil(daysPassed / 7);
      const weekIndexes = Array.from({ length: weeksCount }, (_, i) => i).reverse();

      const labels = weekIndexes.map(w => {
        const start = w * 7 + 1;
        const end = Math.min((w + 1) * 7, daysPassed);
        return `${start}-${end} ${format(now, 'LLLL', { locale: ru })}`;
      });

      const totalsByCategory: Record<string, number[]> = {};

      data.forEach(d => {
        const dt = new Date(d.date);
        if (dt.getFullYear() !== y || dt.getMonth() !== mo) return;
        if (dt.getDate() > daysPassed) return;

        const w = Math.floor((dt.getDate() - 1) / 7);
        const idx = weekIndexes.indexOf(w);
        if (idx < 0) return;

        if (!totalsByCategory[d.name]) totalsByCategory[d.name] = new Array(weeksCount).fill(0);
        totalsByCategory[d.name][idx] += Number(d.total);
      });

      const datasets = Object.entries(totalsByCategory)
        .map(([name, arr]) => ({
          name,
          data: arr,
          sum: arr.reduce((a, b) => a + b, 0),
        }))
        .sort((a, b) => b.sum - a.sum);

      return { labels, datasets };
    }

    if (period === 'week') {
      const weekStart = startOfWeek(now, { locale: ru, weekStartsOn: 1 });
      const daysArr = eachDayOfInterval({ start: weekStart, end: now });
      const daysRev = [...daysArr].reverse();

      const labels = daysRev.map(d => format(d, 'EEEEEE', { locale: ru }));
      const totalsByCategory: Record<string, number[]> = {};

      data.forEach(d => {
        const dt = new Date(d.date);
        const idx = daysRev.findIndex(x => x.toDateString() === dt.toDateString());
        if (idx < 0) return;

        if (!totalsByCategory[d.name]) totalsByCategory[d.name] = new Array(daysRev.length).fill(0);
        totalsByCategory[d.name][idx] += Number(d.total);
      });

      const datasets = Object.entries(totalsByCategory)
        .map(([name, arr]) => ({
          name,
          data: arr,
          sum: arr.reduce((a, b) => a + b, 0),
        }))
        .sort((a, b) => b.sum - a.sum);

      return { labels, datasets };
    }

    if (period === 'custom' && from && to) {
      const start = new Date(from);
      const end = new Date(to);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
        return { labels: [], datasets: [] };
      }

      const totalDays = differenceInCalendarDays(end, start) + 1;
      const totalsByCategory: Record<string, number[]> = {};

      if (totalDays <= 31) {
        const days = eachDayOfInterval({ start, end });
        const labels = days.map(day => format(day, 'dd.MM', { locale: ru }));

        data.forEach(d => {
          const dt = new Date(d.date);
          if (!isWithinInterval(dt, { start, end })) return;

          const idx = days.findIndex(x => x.toDateString() === dt.toDateString());
          if (idx < 0) return;

          if (!totalsByCategory[d.name]) totalsByCategory[d.name] = new Array(days.length).fill(0);
          totalsByCategory[d.name][idx] += Number(d.total);
        });

        const datasets = Object.entries(totalsByCategory)
          .map(([name, arr]) => ({
            name,
            data: arr,
            sum: arr.reduce((a, b) => a + b, 0),
          }))
          .sort((a, b) => b.sum - a.sum);

        return { labels, datasets };
      }

      if (totalDays <= 120) {
        const days = eachDayOfInterval({ start, end });
        const weekMap = new Map<string, number>();
        const labels: string[] = [];

        days.forEach(day => {
          const weekStart = startOfWeek(day, { weekStartsOn: 1 });
          const key = format(weekStart, 'dd.MM.yyyy');
          if (!weekMap.has(key)) {
            weekMap.set(key, weekMap.size);
            labels.push(`${format(weekStart, 'dd.MM')} нед.`);
          }
        });

        data.forEach(d => {
          const dt = new Date(d.date);
          if (!isWithinInterval(dt, { start, end })) return;

          const weekStart = startOfWeek(dt, { weekStartsOn: 1 });
          const key = format(weekStart, 'dd.MM.yyyy');
          const idx = weekMap.get(key);
          if (idx === undefined) return;

          if (!totalsByCategory[d.name])
            totalsByCategory[d.name] = new Array(labels.length).fill(0);
          totalsByCategory[d.name][idx] += Number(d.total);
        });

        const datasets = Object.entries(totalsByCategory)
          .map(([name, arr]) => ({
            name,
            data: arr,
            sum: arr.reduce((a, b) => a + b, 0),
          }))
          .sort((a, b) => b.sum - a.sum);

        return { labels, datasets };
      }

      const months = eachMonthOfInterval({ start, end });
      const labels = months.map(month => format(month, 'LLLL yyyy', { locale: ru }));

      data.forEach(d => {
        const dt = new Date(d.date);
        if (!isWithinInterval(dt, { start, end })) return;

        const idx = months.findIndex(
          month => month.getFullYear() === dt.getFullYear() && month.getMonth() === dt.getMonth(),
        );
        if (idx < 0) return;

        if (!totalsByCategory[d.name]) totalsByCategory[d.name] = new Array(months.length).fill(0);
        totalsByCategory[d.name][idx] += Number(d.total);
      });

      const datasets = Object.entries(totalsByCategory)
        .map(([name, arr]) => ({
          name,
          data: arr,
          sum: arr.reduce((a, b) => a + b, 0),
        }))
        .sort((a, b) => b.sum - a.sum);

      return { labels, datasets };
    }

    return { labels: [], datasets: [] };
  }, [data, period, from, to]);

  const grandSum = useMemo(() => built.datasets.reduce((a, d) => a + d.sum, 0), [built.datasets]);

  const chartData: ChartData<'bar'> = useMemo(
    () => ({
      labels: built.labels,
      datasets: built.datasets.map(d => ({
        label: `${d.name} (${pct(d.sum, grandSum)}%)`,
        data: d.data,
        backgroundColor: hashToColor(d.name, 0.6),
      })),
    }),
    [built.labels, built.datasets, grandSum],
  );

  const options: ChartOptions<'bar'> = useMemo(() => {
    const xTitle =
      period === 'years'
        ? 'Год'
        : period === 'year'
          ? 'Месяц'
          : period === 'month'
            ? 'Неделя'
            : period === 'week'
              ? 'День недели'
              : 'Выбранный период';

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            boxHeight: 12,
            padding: 12,
            font: (ctx: any) => {
              const w = ctx?.chart?.width ?? 400;
              const size = w < 380 ? 11 : 13;
              return { size };
            },
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx: TooltipItem<'bar'>) => {
              const v = Number(ctx.parsed.y ?? 0);
              const label = ctx.dataset.label ?? '';
              const p = grandSum > 0 ? Math.round((v / grandSum) * 100) : 0;

              return `${label}: ${formatCurrency(v)} (${p}%)`;
            },
          },
        },
      },
      scales: {
        x: {
          title: { display: true, text: xTitle },
          ticks: {
            autoSkip: true,
            maxRotation: 0,
            minRotation: 0,
            font: (ctx: any) => {
              const w = ctx?.chart?.width ?? 400;
              const size = w < 380 ? 10 : 12;
              return { size };
            },
          },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: 'Сумма, ₽' },
          ticks: {
            font: (ctx: any) => {
              const w = ctx?.chart?.width ?? 400;
              const size = w < 380 ? 10 : 12;
              return { size };
            },
          },
        },
      },
    };
  }, [period, grandSum]);

  const hasData = useMemo(
    () => built.datasets.some(ds => ds.data.some(v => v > 0)),
    [built.datasets],
  );

  return hasData ? (
    <Box sx={{ width: '100%', height: { xs: 340, sm: 400, md: 460 } }}>
      <Bar data={chartData} options={options} />
    </Box>
  ) : (
    <div>Нет записей для построения графика</div>
  );
};

export default CategoryBar;
